import {
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import {
  IQuestionnaireChoice,
  IQuestionnaireQuestion,
} from 'src/app/interfaces/Questionnaire/IQuestionnaire';

// The points are the number in "(n)" at the start of the answer, 0 to 3.
// Every item has its own answer texts, so never use the answer's position.
const FGA_POINTS_REGEX = /^\s*\(\s*(\d+)\s*\)/;
const FGA_ALLOWED_POINTS = [0, 1, 2, 3];

// Titles separate the instruction with "||" or "‖".
const FGA_TITLE_SEPARATOR = /\|\||‖/;

// The 10 items, 1. Gait level surface to 10. Steps.
const FGA_ITEMS = [
  'question839',
  'question840',
  'question841',
  'question842',
  'question843',
  'question844',
  'question845',
  'question846',
  'question847',
  'question848',
];
// The total is saved but the backend never reads it.
const FGA_TOTAL_CONTROL_NAME = 'question849';
const FGA_TOTAL_MAX_SCORE = 30;

@Component({
  selector: 'app-fga-questions',
  templateUrl: './fga-questions.component.html',
  styleUrl: './fga-questions.component.css',
  standalone: false,
})
export class FgaQuestionsComponent implements OnInit, OnDestroy {
  @Input() elements: IQuestionnaireQuestion[] = [];
  // The page title holds the test's heading and instruction.
  @Input() pageTitle = '';
  @Input() formGroup!: FormGroup;
  @Input() readonlyMode = false;

  private sub?: Subscription;

  constructor(private readonly cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.sub = this.formGroup.valueChanges.subscribe(() => {
      this.recalculateTotal();
    });
    this.recalculateTotal();
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  onRadioClick(controlName: string, value: any): void {
    if (this.readonlyMode) return;
    const control = this.formGroup.get(controlName);
    if (!control) return;

    if (control.value === value) {
      control.setValue(null);
    }
  }

  getQuestionTitle(title?: string): string {
    return title?.split(FGA_TITLE_SEPARATOR)[0]?.trim() ?? '';
  }

  getQuestionInstruction(title?: string): string {
    return title?.split(FGA_TITLE_SEPARATOR)[1]?.trim() ?? '';
  }

  isTotalScoreElement(element: IQuestionnaireQuestion): boolean {
    return element.name === FGA_TOTAL_CONTROL_NAME;
  }

  get maxScore(): number {
    return FGA_TOTAL_MAX_SCORE;
  }

  getScoreDisplay(): string {
    const value = this.formGroup.get(
      FGA_TOTAL_CONTROL_NAME,
    )?.value;
    return value === null || value === undefined || value === ''
      ? '0'
      : String(value);
  }

  // Live total of the items answered so far, or empty while none is, so an
  // untouched test is not saved.
  private recalculateTotal(): void {
    const points = FGA_ITEMS.map((controlName) =>
      this.getSelectedPoints(controlName),
    ).filter((point): point is number => point !== null);
    const total = points.length
      ? points.reduce((sum, point) => sum + point, 0)
      : null;

    const totalCtrl = this.formGroup.get(FGA_TOTAL_CONTROL_NAME);
    if (totalCtrl) {
      totalCtrl.setValue(total === null ? '' : String(total), {
        emitEvent: false,
      });
      totalCtrl.disable({ emitEvent: false });
    }

    this.cdr.detectChanges();
  }

  // Points come from the selected choice text, never from its position.
  private getSelectedPoints(controlName: string): number | null {
    const selectedValue = this.formGroup.get(controlName)?.value;
    if (
      selectedValue === null ||
      selectedValue === undefined ||
      selectedValue === ''
    ) {
      return null;
    }

    const choice = (
      this.elements?.find((el) => el.name === controlName)?.choices ?? []
    ).find((c: IQuestionnaireChoice) => c.value === selectedValue);
    const match = (choice?.text ?? '').match(FGA_POINTS_REGEX);
    const points = match ? Number(match[1]) : null;

    return points !== null && FGA_ALLOWED_POINTS.includes(points)
      ? points
      : null;
  }
}
